import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import React, { useEffect, useMemo } from "react";
// import firebaseToken from "./firebaseToken";
import { useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { themeSettings } from "./theme";
import { ToastContainer } from "react-toastify";

// import Register from "auth/register";
// import ForgotPassword from "auth/forgotPassword";
// import ResetPassword from "auth/resetPassword";
import AuthLayout from "./auth/authLayout";
import Login from "./auth/login";
import Layout from "./scenes/layout";
import Dashboard from "./scenes/dashboard";
import Admin from "./scenes/admin";
import CreateAdmin from "./scenes/admin/createAdmin";
import EditAdmin from "./scenes/admin/editAdmin";
import Role from "./scenes/admin/role";
import CreateRole from "./scenes/admin/role/createRole";
import EditRole from "./scenes/admin/role/editRole";
import Permission from "./scenes/admin/permission";
import User from "./scenes/user";
import CreateUser from "./scenes/user/createUser";
import EditUser from "./scenes/user/editUser";
import UserDetails from "./scenes/user/userDetails";
import Event from "./scenes/event";
import CreateEvent from "./scenes/event/createEvent";
import EditEvent from "./scenes/event/editEvent";
import Package from "./scenes/package";
import CreatePackage from "./scenes/package/createPackage";
import EditPackage from "./scenes/package/editPackage";
import Category from "./scenes/category";
import CreateCategory from "./scenes/category/createCategory";
import EditCategory from "./scenes/category/editCategory";
import CreatePermission from "./scenes/admin/permission/createPermission";
import EditPermission from "./scenes/admin/permission/editPermission";
import Notification from "./scenes/notification";
import CreateNotification from "./scenes/notification/createNotification";
import EditNotification from "./scenes/notification/editNotification";
import Profession from "./scenes/profession";
import MeetUp from "./scenes/meetUp";
import Wallet from "./scenes/wallet";
import UserWalletStatement from "./scenes/wallet/userWalletStatement";
import Withdrawals from "./scenes/wallet/withdrawals";
import CompanyWallet from "./scenes/wallet/companyWallet";
import Transactions from "./scenes/transactions";
import CreateInterest from "./scenes/category/createInterest";
import EditInterest from "./scenes/interest/editInterest";
import UserActivity from "./scenes/userActivity";
import Post from "./scenes/post";
import RatingAndFeedback from "./scenes/rating";
import SupportChat from "./scenes/supportChat";

import Geography from "./scenes/geography";
import Daily from "./scenes/reports/dailyReport";
import Monthly from "./scenes/reports/monthlyReport";
import Performance from "./scenes/performance";
import axios from "axios";
import CreateProfession from "./scenes/profession/createProfession";
import EditProfession from "./scenes/profession/editProfession";
import ProtectedRoute from "./utils/protectedRoute";
import Feedback from "./scenes/feedback";
import WeightCalc from "./scenes/weightCalc";
import EditWeightCalc from "./scenes/weightCalc/editWeightCalc";
import CreateWeightCalc from "./scenes/weightCalc/createWeightCalc";

axios.interceptors.request.use(
  config => {
    config.withCredentials = true;
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["accesstoken"] = token;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Silently refresh the access token on a 401 and retry the original
// request once. If the refresh token is missing/invalid, clear auth
// state and send the user back to login.
let isRefreshingToken = false;
let pendingRequests = [];

const resolvePendingRequests = (newAccessToken) => {
  pendingRequests.forEach(cb => cb(newAccessToken));
  pendingRequests = [];
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  window.location.href = "/auth/login";
};

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (!originalRequest || originalRequest._isRefreshCall) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (!isRefreshingToken) {
        isRefreshingToken = true;
        try {
          const { data } = await axios.post(
            "/auth/admin/refresh",
            { refreshToken },
            { _isRefreshCall: true }
          );
          localStorage.setItem("accessToken", data.accessToken);
          localStorage.setItem("refreshToken", data.refreshToken);
          isRefreshingToken = false;
          resolvePendingRequests(data.accessToken);
        } catch (refreshError) {
          isRefreshingToken = false;
          pendingRequests = [];
          clearAuthAndRedirect();
          return Promise.reject(refreshError);
        }
      }

      return new Promise(resolve => {
        pendingRequests.push(newAccessToken => {
          originalRequest.headers["accesstoken"] = newAccessToken;
          resolve(axios(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

const currentUrl = window.location.href;
const firstSlashIndex = currentUrl.indexOf("/", currentUrl.indexOf("//") + 2);
const baseUrl =
  firstSlashIndex !== -1
    ? currentUrl.substring(0, firstSlashIndex)
    : currentUrl;

if (baseUrl === "http://localhost:3000") {
  console.log("local");
  axios.defaults.baseURL = "http://localhost:8000";
} else if (baseUrl === "https://vetum.ampnetwork.com.np") {
  console.log("production");
  axios.defaults.baseURL = "https://vetumnabkend.ampnetwork.com.np";
}
if (baseUrl === "http://172.31.251.3:9001") {
  console.log("server");
  axios.defaults.baseURL = "http://172.31.251.3:9000";
}

function App() {
  const mode = useSelector(state => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  // useEffect(() => {
  //   firebaseToken();
  //   console.log(firebaseToken());
  // }, []);

  return (
    <div className="app">
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ToastContainer />
          <Routes>
            <Route element={<AuthLayout />}>
              <Route path="/" element={<Navigate to="/auth/login" replace />} />
              <Route path="/auth/login" element={<Login />} />
              {/* <Route path="/auth/register" element={<Register />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} /> */}
            </Route>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/create" element={<CreateAdmin />} />
              <Route
                path="/admin/edit/:adminId"
                element={
                  <ProtectedRoute
                    element={EditAdmin}
                    permissions={["EDIT_ADMIN"]}
                  />
                }
              />
              <Route path="/user" element={<User />} />
              <Route path="/user/create" element={<CreateUser />} />
              <Route path="/user/details/:userId" element={<UserDetails />} />
              <Route
                path="/user/edit/:userId"
                element={
                  <ProtectedRoute
                    element={EditUser}
                    permissions={["EDIT_USER"]}
                  />
                }
              />
              <Route path="/event" element={<Event />} />
              <Route path="/event/create" element={<CreateEvent />} />
              <Route path="/event/edit/:eventId" element={<EditEvent />} />
              <Route path="/package" element={<Package />} />
              <Route path="/package/create" element={<CreatePackage />} />
              <Route
                path="/package/edit/:packageId"
                element={<EditPackage />}
              />
              <Route path="/category" element={<Category />} />
              <Route path="/category/create" element={<CreateCategory />} />
              <Route path="/category/interest" element={<CreateInterest />} />
              <Route
                path="/category/edit/:categoryId"
                element={<EditCategory />}
              />
              <Route path="/notification" element={<Notification />} />
              <Route
                path="/notification/create"
                element={<CreateNotification />}
              />
              <Route
                path="/notification/edit/:notificationId"
                element={<EditNotification />}
              />
              <Route path="/profession" element={<Profession />} />
              <Route path="/profession/create" element={<CreateProfession />} />
              <Route
                path="/profession/edit/:professionId"
                element={<EditProfession />}
              />
              <Route
                path="/interest/edit/:interestId"
                element={<EditInterest />}
              />
              <Route path="/meetup" element={<MeetUp />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/weightCalc" element={<WeightCalc />} />
              <Route
                path="/weightCalc/edit/:id"
                element={<EditWeightCalc />}
              />
              <Route path="/weightCalc/create" element={<CreateWeightCalc />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route
                path="/wallet/history/:userId"
                element={<UserWalletStatement />}
              />
              <Route path="/withdrawals" element={<Withdrawals />} />
              <Route path="/companywallet" element={<CompanyWallet />} />
              <Route path="/role" element={<Role />} />
              <Route path="/role/create" element={<CreateRole />} />
              <Route path="/role/edit/:roleId" element={<EditRole />} />
              <Route path="/permission" element={<Permission />} />
              <Route path="/permission/create" element={<CreatePermission />} />
              <Route
                path="/permission/edit/:permissionId"
                element={<EditPermission />}
              />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/useractivity" element={<UserActivity />} />
              <Route path="/posts" element={<Post />} />
              <Route path="/rating" element={<RatingAndFeedback />} />
              <Route path="/supportchat" element={<SupportChat />} />

              <Route path="/geography" element={<Geography />} />
              <Route path="/daily" element={<Daily />} />
              <Route path="/monthly" element={<Monthly />} />
              <Route path="/performance" element={<Performance />} />
            </Route>
          </Routes>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
